import { useCallback, useEffect, useMemo, useState } from 'react';
import { EntityName } from '../../../auth/Security';
import { FILTER_IDENTIFIERS } from '../../../constants/constants';
import type {
    DeepLinkFilterIdentifier,
    DeepLinkListViewInitialFilter,
    UseDeepLinkListViewInitialFilterReturn
} from '../../types';
import { useDeepLinkInitialState } from './useDeepLinkInitialState';
import { useEntityDeepLinkUIStatePath } from './useEntityDeepLinkUIStatePath';
import { resolveAncestorEntityIdDeepLinkPaths } from './useInitializeDeepLinkUIState.utils';
/**
 * Resolves a one-time deep-link initial filter for list views and exposes a clear handler.
 *
 * @param entity Main entity for deriving default deep-link initial state path.
 * @param entityDeepLinkUIStatePath Optional override dot-path in deep-link initial state to read and consume.
 * @param skip When true, bypasses deep-link initial filter resolution.
 * @param filterIdentifier Identifier to assign to the resolved list filter value.
 * Defaults to `FILTER_IDENTIFIERS.identity`.
 * @returns Current `initialFilter` (if resolved) and `handleClearInitialFilter` callback.
 */
export const useDeepLinkListViewInitialFilter = (
    entity: EntityName,
    entityDeepLinkUIStatePath?: string,
    skip: boolean = false,
    filterIdentifier: DeepLinkFilterIdentifier = FILTER_IDENTIFIERS.identity,
    onAfterClearInitialFilter?: () => void
): UseDeepLinkListViewInitialFilterReturn => {
    const { getDeepLinkInitialState, pluckDeepLinkInitialState } = useDeepLinkInitialState();
    const [initialFilter, setInitialFilter] = useState<DeepLinkListViewInitialFilter>();
    const [isInitialFilterResolved, setIsInitialFilterResolved] = useState(false);
    const isInitialFilterReady = skip || isInitialFilterResolved;

    const resolvedEntityPath = useEntityDeepLinkUIStatePath(entity, entityDeepLinkUIStatePath);
    const resolvedDeepLinkInitialStatePath = `${resolvedEntityPath}.id`;

    useEffect(() => {
        if (skip || isInitialFilterResolved) {
            return;
        }

        const deepLinkedValue = getDeepLinkInitialState<string | undefined>(resolvedDeepLinkInitialStatePath);

        if (deepLinkedValue) {
            const consumedDeepLinkedValue =
                pluckDeepLinkInitialState<string | undefined>(resolvedDeepLinkInitialStatePath) ?? deepLinkedValue;

            const predecessorDeepLinkInitialStatePathSegments = resolveAncestorEntityIdDeepLinkPaths({
                deepLinkUIStatePath: resolvedDeepLinkInitialStatePath,
                includeResolvedPath: false
            });

            // Consume ancestor entity ids in the same deep-link branch so later client-side navigation
            // to parent entity views does not re-apply stale initial filters from the same initial state.
            predecessorDeepLinkInitialStatePathSegments.forEach((path) => {
                pluckDeepLinkInitialState<string | undefined>(path);
            });

            setInitialFilter({
                identifier: filterIdentifier,
                value: consumedDeepLinkedValue
            });
        }

        setIsInitialFilterResolved(true);
    }, [
        filterIdentifier,
        getDeepLinkInitialState,
        isInitialFilterResolved,
        pluckDeepLinkInitialState,
        resolvedDeepLinkInitialStatePath,
        skip
    ]);

    const handleClearInitialFilter = useCallback(() => {
        setInitialFilter(undefined);
        onAfterClearInitialFilter?.();
    }, [onAfterClearInitialFilter]);

    const result = useMemo(
        () => ({
            initialFilter,
            isInitialFilterReady,
            handleClearInitialFilter
        }),
        [handleClearInitialFilter, initialFilter, isInitialFilterReady]
    );

    return result;
};
